/**
 * Centralised model definitions for the Augmented platform.
 * All model lists, provider mappings, and defaults are defined here.
 */

export type ProviderId = 'openrouter' | 'anthropic' | 'openai' | 'google';

export interface ProviderDefinition {
  id: ProviderId;
  label: string;
}

// ---------------------------------------------------------------------------
// Providers
// ---------------------------------------------------------------------------

export const MODEL_PROVIDERS: ProviderDefinition[] = [
  { id: 'openrouter', label: 'OpenRouter' },
  { id: 'anthropic', label: 'Anthropic (Direct)' },
  { id: 'openai', label: 'OpenAI (Direct)' },
  { id: 'google', label: 'Google (Direct)' },
];

// ---------------------------------------------------------------------------
// Available models — the `paid_models` DB catalog is the single source of truth
// ---------------------------------------------------------------------------
//
// ENG-7185: the hardcoded `MODELS` and `OPENROUTER_RECOMMENDED_MODELS` constants
// that used to enumerate the available models per provider were deleted. The
// catalog (`paid_models`, all 4 providers) now drives every picker via the lean
// GET /models/catalog endpoint, and the agent.update_config validators resolve
// against the same catalog (injected as `allowedModels`). What remains here is
// provider metadata + pure id-shape helpers that don't need the model list.

// ---------------------------------------------------------------------------
// Default models per tier (used when agent has no model configured)
// ---------------------------------------------------------------------------

export const DEFAULT_MODELS = {
  primary: 'openrouter/anthropic/claude-opus-4-6',
  secondary: 'openrouter/google/gemini-3.1-flash-lite-preview',
  tertiary: 'openrouter/openai/gpt-5.4-nano',
} as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Build the stored value from provider + model (e.g. "openrouter" + "anthropic/claude-opus-4-6" → "openrouter/anthropic/claude-opus-4-6") */
export function buildStoredModelValue(provider: ProviderId, modelValue: string): string {
  if (provider === 'openrouter') return `openrouter/${modelValue}`;
  return modelValue;
}

/**
 * Extract the provider from a stored model value.
 *
 * OpenRouter ids always carry the `openrouter/` prefix, so they resolve from
 * the string alone. Direct-provider ids are bare (`gpt-4.1`, `claude-opus-4-6`)
 * and can't be classified by shape, so a caller that has loaded the catalog
 * passes it as `catalog` and the row's `provider` wins. With no catalog (or an
 * unknown id) we fall back to `'openrouter'`, matching the prior default.
 */
export function deriveProviderFromModel(
  storedValue: string,
  catalog?: ReadonlyArray<{ value: string; provider: ProviderId }>,
): ProviderId {
  if (storedValue.startsWith('openrouter/')) return 'openrouter';
  const row = catalog?.find((m) => m.value === storedValue);
  if (row) return row.provider;
  return 'openrouter';
}

/** Extract the model-specific part from a stored value */
export function deriveModelValue(storedValue: string, provider: ProviderId): string {
  if (provider === 'openrouter' && storedValue.startsWith('openrouter/')) {
    return storedValue.slice('openrouter/'.length);
  }
  return storedValue;
}

// ---------------------------------------------------------------------------
// Claude Code (subscription) model aliases — ENG-5631
// ---------------------------------------------------------------------------
//
// Claude Code agents run on the operator's Claude subscription, so the model
// is chosen by *family alias* (`fable`/`opus`/`sonnet`/`haiku`) rather than a
// dated SKU or a routed provider/model pair. The launcher passes the alias to
// `claude --model <alias>` (apps/cli/src/lib/persistent-session.ts) — the only
// mechanism that actually takes effect for subscription agents. Using the
// alias (not a dated name like `claude-opus-4-7`) tracks the recommended
// version per family and won't fall through when a dated model is retired.
// `fable` (Fable 5, `claude-fable-5`) is the top tier above Opus.
//
// Deliberately NOT modelled as an entry in MODEL_PROVIDERS/MODELS: those drive
// the org/platform tier-default pickers too, where a bare alias would be an
// invalid value for OpenClaw/NemoClaw agents that route via OpenRouter and
// need a full model id. The alias picker is scoped to claude-code agents in
// the edit-agent UI instead.

export type ClaudeModelAlias = 'fable' | 'opus' | 'sonnet' | 'haiku';

/**
 * Picker option for the edit-agent model dropdown. The bracket variant
 * `opus[fast]` is an Opus family member that additionally opts the session
 * into Anthropic's fast-output mode (ENG-5770) via `/fast` after boot — the
 * launcher still passes bare `opus` to `--model` because `[fast]` is not a
 * model name, it's a runtime mode the manager toggles via slash command.
 */
export type ClaudeModelOption = 'fable' | 'opus' | 'opus[fast]' | 'sonnet' | 'haiku';

/** Alias options shown in the edit-agent model picker for claude-code agents. */
export const CLAUDE_CODE_MODEL_OPTIONS: ReadonlyArray<{ value: ClaudeModelOption; label: string }> = [
  // Fable is the top tier (above Opus); it has no `[fast]` variant — fast-output
  // mode (`/fast`) is currently only valid on Opus 4.6/4.7/4.8.
  { value: 'fable', label: 'Fable' },
  { value: 'opus', label: 'Opus' },
  { value: 'opus[fast]', label: 'Opus (Fast)' },
  { value: 'sonnet', label: 'Sonnet' },
  { value: 'haiku', label: 'Haiku' },
];

// ---------------------------------------------------------------------------
// Paid models catalog + token markup (ENG-7163, ENG-7185)
// ---------------------------------------------------------------------------
//
// The `paid_models` table is the single source of truth for the offered model
// catalog across all 4 providers (ENG-7185 deleted the hardcoded MODELS /
// OPENROUTER_RECOMMENDED_MODELS constants it superseded). A row carries the
// picker metadata; per-MTok cost is joined from model_token_costs, and the
// price shown to a customer applies a markup that layers per-model -> per-org
// -> platform -> 1.0. The small-fast companion lives on the row
// (paid_models.small_fast_model) and is resolved at /host/refresh.

/** A row of the `paid_models` catalog (DB shape, camelCased for callers). */
export interface PaidModel {
  /** Full stored model id (PK); joins model_token_costs.model. */
  model: string;
  provider: ProviderId;
  displayName: string;
  /** Offered to customers in the picker. */
  enabled: boolean;
  /** Ascending display order. */
  sortOrder: number;
  /** Curated cheaper companion (ANTHROPIC_SMALL_FAST_MODEL); null = reuse primary. */
  smallFastModel: string | null;
  /** Per-model markup override (multiplier); null defers to org/platform/1.0. */
  markup: number | null;
}

/** Neutral markup multiplier — raw cost, no markup. The safe default. */
export const DEFAULT_TOKEN_MARKUP = 1.0;

/**
 * Resolve the effective token-cost markup multiplier by layering the three
 * override grains, most-specific first: per-model -> per-org -> platform
 * default -> 1.0 (raw cost). Each input is optional/nullable; a value only wins
 * if it's a finite, non-negative number, so a NULL column, a missing org
 * setting, or a malformed value all fall through to the next grain rather than
 * producing a NaN price. 1.0 is the safe floor: an unconfigured platform shows
 * raw cost, never an accidental markup.
 */
export function resolveTokenMarkup(
  perModel?: number | null,
  orgMarkup?: number | null,
  platformMarkup?: number | null,
): number {
  for (const candidate of [perModel, orgMarkup, platformMarkup]) {
    if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate >= 0) {
      return candidate;
    }
  }
  return DEFAULT_TOKEN_MARKUP;
}

/**
 * Reduce a platform model identifier to its Claude Code family alias, or
 * `null` when the input is empty or doesn't name a known Claude family. Used
 * by the launcher to build the `--model <alias>` flag and by the edit-agent
 * UI to display a claude-code agent's currently-stored model (which may be a
 * legacy full name like `claude-sonnet-4-6`) as its alias.
 *
 * Handles: dated full names (`claude-opus-4-7`), context-window variants
 * (`claude-opus-4-7[1m]` → bare alias; the auth tier decides 1M availability),
 * fast-mode marker (`opus[fast]` → bare `opus`; /fast is sent at boot by the
 * manager, not via `--model`), legacy `openrouter/anthropic/` routing
 * prefixes, and bare aliases.
 */
export function claudeModelAlias(primaryModel?: string | null): ClaudeModelAlias | null {
  if (!primaryModel) return null;

  // Normalise: lower-case, then drop any provider routing prefix
  // (`openrouter/anthropic/claude-sonnet-4-6` → `claude-sonnet-4-6`).
  const name = (primaryModel.split('/').pop() ?? '').trim().toLowerCase();
  if (!name) return null;

  // Match on the family word so version suffixes (`-5`, `-4-6`) and context-window
  // markers (`[1m]`) are ignored, and a bare alias (`sonnet`) still resolves.
  // The families are mutually exclusive.
  if (name.includes('fable')) return 'fable';
  if (name.includes('opus')) return 'opus';
  if (name.includes('sonnet')) return 'sonnet';
  if (name.includes('haiku')) return 'haiku';

  return null;
}

/**
 * Resolve a stored `primary_model` to its picker option — preserves the
 * `opus[fast]` distinction the dropdown needs. The plain alias resolver
 * collapses both `opus` and `opus[fast]` to `opus`, so the edit-agent UI
 * needs this richer form to show the operator's actual selection.
 */
export function claudeModelOption(primaryModel?: string | null): ClaudeModelOption | null {
  const alias = claudeModelAlias(primaryModel);
  if (!alias) return null;
  if (alias === 'opus' && isClaudeFastMode(primaryModel)) return 'opus[fast]';
  return alias;
}

/**
 * True when the stored `primary_model` carries the `[fast]` marker. Used by
 * the manager to decide whether to send `/fast` after the Claude Code ready
 * banner. The marker is a generic suffix — gated to Opus at the picker layer
 * (Sonnet/Haiku don't currently support /fast), and re-checked at send time
 * against the live banner to avoid sending after a silent model downgrade.
 */
export function isClaudeFastMode(primaryModel?: string | null): boolean {
  if (!primaryModel) return false;
  return /\[fast\]/i.test(primaryModel);
}

// ---------------------------------------------------------------------------
// Model-value validation (ENG-6425)
// ---------------------------------------------------------------------------
//
// Used by the admin-debug `agent.update_config` write path to reject an invalid
// model before it is persisted (route, request-time) and again before it is
// applied (broker, execution-time). The legal value space depends on the
// agent's framework: claude-code (subscription) agents take a bare family alias
// (`CLAUDE_CODE_MODEL_OPTIONS`), every other framework takes a full stored model
// id from the `paid_models` catalog (openrouter values carry the `openrouter/`
// prefix; the direct providers are bare). Putting an openrouter id on a
// subscription agent — or a bare alias on an openrouter agent — is exactly the
// misconfiguration these guards exist to prevent.

/** True iff `value` is a valid Claude Code subscription model picker option. */
export function isClaudeCodeModelOption(value: string): value is ClaudeModelOption {
  return CLAUDE_CODE_MODEL_OPTIONS.some((o) => o.value === value);
}

/**
 * Validate a proposed model value for an agent of the given framework.
 * `claude-code` → bare family alias, UNLESS the agent's host is in OpenRouter
 * mode (ENG-7152), in which case it takes a stored `openrouter/...` id instead
 * (the host overrides the subscription with a base-URL + per-agent key). Any
 * other framework → full stored model id. Returns false for an unknown value
 * (or one of the wrong shape), so the caller can reject it before the DB.
 *
 * ENG-7185: the legal model set IS the `paid_models` catalog — there is no
 * static fallback any more (MODELS / OPENROUTER_RECOMMENDED_MODELS were deleted).
 * A caller reads the enabled catalog ids and passes them as `opts.allowedModels`
 * (the full set is fine for both routed frameworks and claude-code OpenRouter
 * mode; a bare alias never matches an `openrouter/...` id and vice versa). The
 * list is authoritative when PRESENT (a successful read returning `[]` rejects
 * every model). An OMITTED list (`undefined` - the catalog couldn't be read)
 * fails CLOSED: a model that needs catalog validation is rejected rather than
 * waved through, so a catalog outage can't let an arbitrary model be persisted.
 */
export function isValidModelForFramework(
  framework: string | null | undefined,
  value: string,
  opts?: { hostOpenRouter?: boolean; allowedModels?: readonly string[] },
): boolean {
  if (framework === 'claude-code' && !opts?.hostOpenRouter) {
    return isClaudeCodeModelOption(value);
  }
  // Routed framework, or claude-code in OpenRouter mode: the value must be an
  // enabled catalog model. Fail closed when the catalog list is unavailable.
  const allowed = opts?.allowedModels;
  if (allowed === undefined) return false;
  // ENG-7185: a claude-code agent on an OpenRouter host must use a real
  // `openrouter/...` id. The catalog now also holds bare direct-provider ids
  // (gpt-4.1, claude-opus-4-6, …) which the subscription override must never
  // persist on an OpenRouter host, so reject anything without the prefix.
  if (framework === 'claude-code' && opts?.hostOpenRouter && !value.startsWith('openrouter/')) {
    return false;
  }
  return allowed.includes(value);
}
